import { useCallback, useEffect, useRef, useState } from 'react';
import useApiRequest from '../../../lib/hooks/useRequest';
import { OfferListItemResponseDto, OfferListPaginatedResponseDto } from '../types/response';
import { useOfferListParams } from './use-offer-list-params';

/** `GET offers/admin`, driven by filter and page in the URL – same pattern as `products/hooks/use-archive-list.ts`. */
export const useOfferList = () => {
  const { apiRequest } = useApiRequest();
  const { filter, handleFilterChange, pagination, isValidPagination, handlePaginationChange } = useOfferListParams();
  const { pageIndex, pageSize } = pagination;

  const [items, setItems] = useState<OfferListItemResponseDto[]>([]);
  const [pageCount, setPageCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Bumped per request; a response for an outdated filter or page is discarded.
  const requestIdRef = useRef(0);

  /** `silent` keeps the current rows on screen – used for the refetch after sending. */
  const fetchList = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      const requestId = ++requestIdRef.current;
      if (!silent) setIsLoading(true);

      const query = new URLSearchParams({ filter, page: String(pageIndex + 1), limit: String(pageSize) });
      const res = await apiRequest<OfferListPaginatedResponseDto>(`offers/admin?${query}`, 'GET');
      if (requestId !== requestIdRef.current) return;

      if (res.data) {
        setItems(res.data.docs);
        setPageCount(res.data.pageCount);
      } else if (!silent) {
        // Never leave rows of the previous filter or page under the new one.
        setItems([]);
        setPageCount(0);
      }
      setIsLoading(false);
    },
    [apiRequest, filter, pageIndex, pageSize]
  );

  useEffect(() => {
    if (!isValidPagination) return;
    fetchList();
  }, [fetchList, isValidPagination]);

  const reload = useCallback(() => fetchList({ silent: true }), [fetchList]);

  return { items, pageCount, isLoading: isLoading || !isValidPagination, reload, filter, handleFilterChange, pagination, handlePaginationChange };
};
